import { h } from 'preact';
import { withText, Text } from 'preact-i18n';
import * as styles from './panel.scss';
import * as globalStyles from '../../styles/global.scss';
import { useMemo, useRef, useState } from 'preact/hooks';
import { SearchField } from '../search-field/search-field';
import { AccuracyLabel } from './accuracy-label';
import { ClosePanel } from '../icons/close-panel';
import { TranscriptContainer } from './transcript-container';
import { MentionedTerms } from './mentioned-terms';
import { VerificationStatus } from '../../types/verification-status';
import type { DataProvider } from '../../providers/data-provider';
import type { CuePoint } from '../../types/cuepoint';

const translations = {
  closePanelButton: <Text id="taxonomyTranscript.closePanelButton">Close Transcript Panel</Text>
};

interface Props {
  onCloseButtonClick: () => void;
  dataProvider: DataProvider;
  isActive: boolean;
  player: KalturaPlayerTypes.Player;
  activeCuePoint: CuePoint | null;
}
export const Panel = withText(translations)(({
  closePanelButton,
  onCloseButtonClick,
  dataProvider,
  isActive,
  player,
  activeCuePoint
}: Props & { closePanelButton: string }) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [activeTerm, setActiveTerm] = useState<string | null>(null);
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(1);
  const transcriptPanelContainerRef = useRef<HTMLDivElement>(null);

  const handleKeywordChange = (value: string): void => {
    setSearchValue(value);
    setActiveTerm(null);
  };

  function handleTermClick(term: string): void {
    setCurrentSearchIndex(1);

    if (activeTerm === term) {
      // reset
      setSearchValue('');
      setActiveTerm(null);

      return;
    }

    setSearchValue(term);
    setActiveTerm(term);
  }

  if (!isActive) {
    return null;
  }

  function handleTranscriptTextClick(time: number): void {
    player.currentTime = time;
  }

  function handleSearchIndexChange(index: number) {
    setCurrentSearchIndex(index);
  }

  const verificationStatus = dataProvider.getVerificationStatus();
  const captionsAsCuePoints = useMemo(() => {
    return dataProvider.getCaptionsCuePoints();
  }, [dataProvider]);
  const { searchResultsCount, matchingCuePointId } = useMemo(() => {
    if (searchValue === '') {
      return { searchResultsCount: 0, matchingCuePointId: null };
    }

    const matchingCuePoints = captionsAsCuePoints.filter((cuePoint) => cuePoint.metadata.text.toLowerCase().includes(searchValue.toLowerCase()));

    return {
      searchResultsCount: matchingCuePoints.length,
      matchingCuePointId:
        currentSearchIndex > 0 && matchingCuePoints.length > 0 && matchingCuePoints[currentSearchIndex - 1]
          ? matchingCuePoints[currentSearchIndex - 1].id
          : null
    };
  }, [captionsAsCuePoints, currentSearchIndex, searchValue]);
  const mentionedTerms = dataProvider.getMentionedTerms();

  return (
    <div ref={transcriptPanelContainerRef} className={styles.transcriptPanelContainer}>
      <div className={styles.searchAndCloseButtonRow}>
        <SearchField
          currentSearchIndex={currentSearchIndex}
          searchResultsCount={searchResultsCount}
          searchValue={searchValue}
          onChange={handleKeywordChange}
          onSearchIndexChange={handleSearchIndexChange}
        />
        <button className={styles.closeButton} onClick={onCloseButtonClick}>
          <span aria-hidden>
            <ClosePanel />
          </span>
          <span className={globalStyles.visuallyHidden}>{closePanelButton}</span>
        </button>
      </div>
      <MentionedTerms activeTerm={activeTerm} terms={mentionedTerms} onTermClick={handleTermClick} />
      {verificationStatus !== VerificationStatus.Empty ? <AccuracyLabel verificationStatus={verificationStatus} /> : null}
      <TranscriptContainer
        activeCuePoint={activeCuePoint}
        cuePoints={captionsAsCuePoints}
        highlightedWord={activeTerm || searchValue}
        scrollToCuePointId={matchingCuePointId}
        transcriptContainerRef={transcriptPanelContainerRef}
        onTextLineClick={handleTranscriptTextClick}
      />
    </div>
  );
});
